import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Type-ahead combobox. Props:
//  options: [{ id, label, sub }]  |  onSelect(option)  |  placeholder  |  value (id)
//  selectedLabel: text to show when the chosen value is not in the current options
//  onSearch(query) optional (server-side search)  |  loading
//  allowNew: offer '+ Add "query"' when nothing matches -> onNew(query)
//
// You type straight into the field. ↑/↓ move through the list, Enter picks the
// highlighted match, Esc closes. With allowNew nothing is highlighted at first,
// so Enter adds the typed text as new — two people can share a name, so a
// match is only taken when the user points at it.
export default function SearchableSelect({
  options = [],
  value,
  onSelect,
  placeholder = 'Search…',
  searchable = true,
  display,
  onSearch,
  loading = false,
  allowNew = false,
  onNew,
  disabled = false,
  selectedLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Matches the menu's min-width below (16rem), in pixels.
  const MENU_MIN = 256;
  const noPick = allowNew ? -1 : 0; // starting highlight
  const [active, setActive] = useState(noPick);
  const [box, setBox] = useState(null); // where to draw the list, in screen coords
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const menuRef = useRef(null);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    const onClick = (e) => {
      const inField = ref.current?.contains(e.target);
      const inList = menuRef.current?.contains(e.target);
      if (!inField && !inList) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    // With server-side search the options already match the query (by name,
    // mobile, email…); filtering again by label would hide mobile matches.
    if (onSearch) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label || o.name || '').toLowerCase().includes(q));
  }, [options, query, onSearch]);

  // Parents rebuild `options` every render, so key the reset on its length.
  useEffect(() => { setActive(noPick); }, [query, filtered.length, noPick]);

  // A server-searched list changes with every keystroke, so the chosen option
  // may not be in it; fall back to the label the parent knows.
  const found = options.find((o) => Number(o.id) === Number(value)) || null;
  const selected = found || (value && selectedLabel ? { id: value, label: selectedLabel } : null);
  const selectedText = selected ? (display ? display(selected) : selected.label || selected.name) : '';

  const search = (q) => {
    setQuery(q);
    if (onSearch) onSearch(q);
  };
  // Where the list goes: under the field, as wide as it, never narrower than
  // MENU_MIN and never past either edge of the window. If there is more room
  // above than below, it opens upwards.
  const place = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const gap = 4;
    const width = Math.min(Math.max(r.width, MENU_MIN), window.innerWidth - 16);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const below = window.innerHeight - r.bottom - gap - 8;
    const above = r.top - gap - 8;
    const dropUp = below < 180 && above > below;
    setBox({
      left,
      width,
      top: dropUp ? undefined : r.bottom + gap,
      bottom: dropUp ? window.innerHeight - r.top + gap : undefined,
      maxHeight: Math.max(120, (dropUp ? above : below)),
    });
  }, []);

  const openList = () => {
    if (disabled || open) return;
    place();
    setOpen(true);
    search('');
  };

  // Follow the field while the page moves under it.
  useEffect(() => {
    if (!open) return undefined;
    place();
    const on = () => place();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on, true);
      window.removeEventListener('resize', on);
    };
  }, [open, place]);
  const choose = (o) => {
    onSelect(o);
    close();
    inputRef.current?.blur();
  };
  const canAdd = allowNew && !!onNew && !loading && !!query.trim();
  const addNew = () => {
    const text = query.trim();
    if (!text || !onNew) return;
    close();
    inputRef.current?.blur();
    onNew(text);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, noPick));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) { openList(); return; }
      if (loading) return;
      if (active >= 0 && filtered[active]) choose(filtered[active]);
      else if (canAdd) addNew();
      else if (!allowNew && filtered[0]) choose(filtered[0]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      close();
    }
  };

  // Keep the highlighted row in view.
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <div className="relative" ref={ref}>
      {searchable ? (
        <div className="relative">
          <input
            ref={inputRef}
            disabled={disabled}
            className="input pr-7"
            // Closed: show the chosen item. Open: show what is being typed.
            value={open ? query : selectedText}
            placeholder={open && selectedText ? selectedText : placeholder}
            onFocus={openList}
            onClick={openList}
            onChange={(e) => { if (!open) setOpen(true); search(e.target.value); }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-content-subtle">▾</span>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onKeyDown}
          className="input flex items-center justify-between text-left"
        >
          <span className={selected ? '' : 'text-content-subtle'}>{selectedText || placeholder}</span>
          <span className="text-content-subtle">▾</span>
        </button>
      )}

      {open && box && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: box.left,
            top: box.top,
            bottom: box.bottom,
            width: box.width,
          }}
          className="z-[70] overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
        >
          <ul
            ref={listRef}
            style={{ maxHeight: Math.min(256, box.maxHeight) }}
            className="overflow-y-auto py-1"
            role="listbox"
          >
            {loading && <li className="px-3 py-2 text-xs text-content-subtle">Searching…</li>}
            {!loading && filtered.length === 0 && !canAdd && (
              <li className="px-3 py-2 text-xs text-content-subtle">
                {query.trim() ? 'No match' : 'Nothing to show'}
              </li>
            )}
            {canAdd && (
              <li>
                <button
                  type="button"
                  className={`w-full border-b border-line px-3 py-2 text-left text-sm font-semibold text-brand-700 ${active === -1 ? 'bg-brand-100' : 'hover:bg-brand-50'}`}
                  onMouseEnter={() => setActive(-1)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={addNew}
                >
                  + Add “{query.trim()}” <span className="font-normal text-content-subtle">— new, press Enter</span>
                </button>
              </li>
            )}
            {!loading && filtered.map((o, i) => (
              <li key={o.id} data-idx={i}>
                <button
                  type="button"
                  className={`flex w-full flex-col px-3 py-2 text-left text-sm ${i === active ? 'bg-brand-50' : 'hover:bg-surface-hover'}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                >
                  <span className="font-medium text-content">{o.label || o.name}</span>
                  {o.sub && <span className="text-xs text-content-subtle">{o.sub}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
